import React, { Component, createContext, useContext } from 'react';
import { AuthenticationContext, authenticationStates } from '../msal/AuthenticationContext';
import { TenantContext } from './TenantContext';
import { ErrorHandlingContext } from './ErrorHandlingContext';
import * as Utils from '../utils/helper';
import { getOrganizationDetails } from '../graph/GraphService';

export const AuthorizationContext = createContext();

// Enum defines the possible states of the authorizationState property
export const authorizationStates = {
    UNKNOWN: 'Unknown', // The authorization has not been checked yet
    CHECKING: 'Checking', // Currently checking authorization with the backend
    AUTHORIZED: 'Authorized', // The currently logged-in user is authorized to use the selected tenant
    UNAUTHORIZED: 'Unauthorized' // The currently logged-in user is not authorized to use the selected tenant
}

class AuthorizationProviderInternal extends Component {
    static contextType = ErrorHandlingContext;
    _isCheckingAuthorization = false;
    _lastCheckedTenantId = null;

    constructor(props) {
        super(props);

        this.state = {
            authorizationState: authorizationStates.UNKNOWN,
            detailedAuthorizationState: {},
            isAuthorized: () => { return this.state.authorizationState === authorizationStates.AUTHORIZED },
            checkAuthorization: async () => { await this.checkAuthorization() },
        };
    }

    componentDidMount() {
        this._isMounted = true;
        this.checkAuthorizationIfNeeded();
    }

    componentDidUpdate(prevProps) {
        // Re-check authorization when tenant changes
        if (prevProps.tenantContext.tenantId !== this.props.tenantContext.tenantId) {
            console.log(`[AuthorizationProvider] Tenant changed from ${prevProps.tenantContext.tenantId} to ${this.props.tenantContext.tenantId}, checking authorization...`);
            this.checkAuthorizationIfNeeded();
        }

        // Re-check when authentication state changes
        if (prevProps.authenticationContext.getAuthenticationState() !== this.props.authenticationContext.getAuthenticationState()) {
            this.checkAuthorizationIfNeeded();
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    checkAuthorizationIfNeeded = () => {
        if (!this._isMounted) {
            return;
        }

        const authState = this.props.authenticationContext.getAuthenticationState();
        const tenantId = this.props.tenantContext.tenantId;

        // Clear authorization if not authenticated
        if (authState !== authenticationStates.AUTHENTICATED) {
            if (authState !== authenticationStates.AUTHENTICATING && 
                this.state.authorizationState !== authorizationStates.UNKNOWN) {
                console.log(`[AuthorizationProvider] User not authenticated, clearing authorization state.`);
                this._lastCheckedTenantId = null;
                this.setState({
                    authorizationState: authorizationStates.UNKNOWN,
                    detailedAuthorizationState: {}
                });
            }
            return;
        }

        // Don't check if no tenant is selected
        if (!tenantId || tenantId === "") {
            console.log(`[AuthorizationProvider] No tenant selected, skipping authorization check.`);
            return;
        }

        // Don't re-check if we're already checking or already checked this tenant
        if (this._isCheckingAuthorization) {
            return;
        }
        if (this._lastCheckedTenantId === tenantId && 
            this.state.authorizationState !== authorizationStates.UNKNOWN) {
            return;
        }

        // Check authorization for the current tenant
        this.checkAuthorization();
    }

    checkAuthorization = async () => {
        if (!this._isMounted) {
            return;
        }

        const tenantId = this.props.tenantContext.tenantId;
        
        if (!tenantId || tenantId === "") {
            console.log(`[AuthorizationProvider, checkAuthorization] No tenant selected, cannot check authorization.`);
            return;
        }

        // Only check if authenticated
        if (this.props.authenticationContext.getAuthenticationState() !== authenticationStates.AUTHENTICATED) {
            console.log(`[AuthorizationProvider, checkAuthorization] User not authenticated, skipping authorization check.`);
            return;
        }

        // Guard against concurrent calls
        if (this._isCheckingAuthorization) {
            return;
        }
        this._isCheckingAuthorization = true;
        this._lastCheckedTenantId = tenantId;

        console.log(`[AuthorizationProvider, checkAuthorization] Checking authorization for tenant ${tenantId}...`);
        
        this.setState({ authorizationState: authorizationStates.CHECKING });

        let authorizationInfo = await this.checkAuthorizationOnBackend(tenantId);

        const authorizationState = Utils.isTrue(authorizationInfo.isUserAuthorized) ? 
                                    authorizationStates.AUTHORIZED : 
                                    authorizationStates.UNAUTHORIZED;
        
        console.log(`[AuthorizationProvider, checkAuthorization] Authorization state for tenant ${tenantId}: ${authorizationState}`);    
                
        if (this._isMounted) {
            this.setState({
                authorizationState: authorizationState,
                detailedAuthorizationState: authorizationInfo                
            });
        }

        this._isCheckingAuthorization = false;
    }

    // Method retrieves the authorization information from the back-end.
    // Response looks like this:
    // {
    //   "isOrganizationAuthorized": true,
    //   "isUserAuthorized": true
    // }
    checkAuthorizationOnBackend = async (tenantId) => {
        try {
            console.log(`[AuthorizationProvider, checkAuthorizationOnBackend] Checking the authorization of the user on the back-end for tenant ${tenantId}...`);

            const accessToken = await this.props.authenticationContext.getApiTokenAsync();

            const response = await fetch(`api/tenant/${tenantId}/authorize`, {
                headers: { 'Authorization': `Bearer ${accessToken.accessToken}` }
            });

            if (response.status === 200) {
                const data = await response.json();
                return data;
            }

            // All other responses from the API are handled as an unexpected error.
            throw new Error(`Server returned unexpected status code ${response.status}`);
        }
        catch (err) {
            this.context.setError("Could not verify user authorization at the server.", err);            
        }

        return {};
    }

    isEmptyOrSpaces = (str) => {
        return !str || typeof str !== "string" || str.match(/^ *$/) !== null;
    }

    // Method performs a best-effort attempt to extract the organization name
    // from the provided username.
    extractOrganizationNameFromUsername = (fullName) => {
        if (this.isEmptyOrSpaces(fullName)) {
            return "";
        }

        const atSignPosition = fullName.indexOf('@');
        if (atSignPosition === -1) {
            // This does not look like an e-mail address, return the full string instead.
            return fullName;
        }

        // Extract everything after the '@' sign.
        const fullDomain = fullName.substring(atSignPosition + 1);

        // The default domain name when an Azure account is created is '<customername>.onmicrosoft.com'.
        // When this pattern is recognized, the 'customername' part is extracted and returned.
        const microsoftDefaultDomain = '.onmicrosoft.com';
        if (fullDomain.toLowerCase().endsWith(microsoftDefaultDomain)) {
            return fullDomain.substring(0, fullDomain.length - microsoftDefaultDomain.length);
        }

        // The customer has added its custom domain to its Azure account, so the fullDomain contains 
        // a domain like 'contoso.com'. Try to remove the top-level domain ('.com') and return the result.
        const lastDotPosition = fullDomain.lastIndexOf('.');
        if (lastDotPosition === -1) {
            // No top-level domain found, return the full domain.
            return fullDomain;
        }

        // Return everything up to the last dot.
        return fullDomain.substring(0, lastDotPosition);
    }

    // Method attempts to retrieve the organization display name for the currently logged-in user from the Graph API.
    // Testing showed that this often fails when the user has just given consent (which always happens when the 
    // tenant reaches the management portal for the first time). In that case the AccessToken for the Graph API is 
    // successfully retrieved, but does not contain the 'User.Read' scope. It takes more than 15 seconds before the 
    // consent is fully processed at Microsoft and a complete AccessToken is returned. This is too long to have 
    // the user waiting so a fallback is implemented. 
    // This fallback is to retrieve the organization name from the username using the 'extractOrganizationNameFromUsername' 
    // method.
    getOrganizationDisplayName = async () => {
        try {
            console.log("[AuthorizationProvider, getOrganizationDisplayName] Invoking the Graph API to get the organization name of this tenant.");

            const accessToken = await this.props.authenticationContext.getGraphApiTokenAsync();
            var organization = await getOrganizationDetails(accessToken);

            if (organization && organization.value && organization.value.length > 0) {
                return organization.value[0].displayName;
            }

            console.log(`[AuthorizationProvider, getOrganizationDisplayName] Error: Could not extract the organization name from the response: ${JSON.stringify(organization)}`);
        }
        catch (err) {
            // The global error is not set here, because failure of retrieving the organization name should not 
            // break the sign-up process.
            console.log(`[AuthorizationProvider, getOrganizationDisplayName] Error while retrieving organization name for tenant: ${JSON.stringify(err)}`);
        }

        // In case the displayname of the tenant could not be retrieved, perform a best-effort attempt to get the 
        // organization name from the full username.
        const userAccount = this.props.authenticationContext.user;
        if (userAccount) {
            console.log("[AuthorizationProvider, getOrganizationDisplayName] Organization name could not be retrieved from the Graph API, now extracting the name from the full username.");
            return this.extractOrganizationNameFromUsername(userAccount.username);
        }

        return "Unknown";
    }

    render() {
        return (
            <AuthorizationContext.Provider value={this.state}>
                {this.props.children}
            </AuthorizationContext.Provider>
        )
    }
}

export const AuthorizationProvider = (props) => {
    const authenticationContext = useContext(AuthenticationContext);
    const tenantContext = useContext(TenantContext);
  
    return (
        <AuthorizationProviderInternal 
            authenticationContext={authenticationContext} 
            tenantContext={tenantContext} 
            {...props}
        />
    )
}
